import { Directive, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { ExcelHelper } from '@helpers/excel.helper';
import { TranslateService } from '@ngx-translate/core';
import { CustomDateTimeByUtcPipe } from '@shared/pipes-shared/pipes';
import * as XLSX from 'xlsx';
import { PaginationDynamicComponent } from '../pagination-dynamic/pagination-dynamic.component';
import { DocumentsService } from '@services/documents.service';

@Directive()
export abstract class PaginationDynamicWithExportComponent<T, S> extends PaginationDynamicComponent<T, S> implements OnInit, OnDestroy {
  protected abstract fileNameLabel: string;
  protected abstract batchReferenceNumber: string;
  protected abstract documentIds: number[];
  protected abstract translateService: TranslateService;
  protected abstract customDateTime: CustomDateTimeByUtcPipe;
  protected abstract documentsService?: DocumentsService;

  public exportToExcel(): void {
    this.searchMethod({ ...this.lastSearch, limit: null, offset: 0 }, true)
      .pipe(
        switchMap((result) => forkJoin({ tableData: this.tableData(result.items), fileName: this.fileName })),
        takeUntil(this.isDestroy),
      )
      .subscribe(({ tableData, fileName }) => {
        ExcelHelper.exportAsExcel(tableData, fileName, this.getTableSettings());
      });
  }

  public exportBatch(): void {
    const referenceNumber = this.batchReferenceNumber || '';
    const documentIds = this.documentIds || [];
    const zipFileName = `${this.documentsService.formatZipFileName(this.translateService.instant(this.fileNameLabel))}`;
    this.getArchive(documentIds, referenceNumber)
      .pipe(take(1))
      .subscribe((file: Blob) => {
        this.documentsService.downloadBlobFile(file, zipFileName);
      });
  }

  protected abstract getArchive(documentIds: number[], referenceNumber?: string): Observable<Blob>;
  protected abstract getTableRow(item: T, index: number): Observable<Array<number | string>>;
  protected abstract getFilters(): Observable<Array<number | string>>;
  protected abstract getTableHeaders(): Observable<Array<number | string>>;
  protected abstract getTableSettings(): {
    cols?: XLSX.ColInfo[];
    merges?: XLSX.Range[];
  };

  protected getTableRows(list: T[]): Observable<Array<Array<number | string>>> {
    return forkJoin(list.map((item, index) => this.getTableRow(item, index)));
  }

  private tableData(list: T[]): Observable<Array<Array<number | string>>> {
    return forkJoin({
      filters: this.getFilters(),
      headers: this.getTableHeaders(),
      rows: this.getTableRows(list),
    }).pipe(map((data) => [data.filters.length ? [data.filters.join(', ')] : [], data.headers, ...data.rows]));
  }

  private get fileName(): Observable<string> {
    return forkJoin({ title: this.translateService.get(this.fileNameLabel), filters: this.getFilters() }).pipe(
      map(({ title, filters }) => `${title} ${filters.join('; ')}.xlsx`),
    );
  }
}
